import { Input, InputRef } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDebouncedCallback } from "use-debounce"
import { DEBOUNCE_DURATION_LONG } from "Shared/config/consts"
import { executeAfterAsyncDomUpdates } from "Shared/utils/helper.utils"

const { Search } = Input
interface SearchInputInterface {
    onSearch: (text: string) => void
    isLoading?: boolean
    value?: string
    className?: string
    hidden?: boolean
}

const SearchInput = ({ onSearch, isLoading = false, value = "", className = "", hidden = false }: SearchInputInterface) => {
    const inputRef = useRef<InputRef>(null)
    const { t } = useTranslation()

    const [isOpen, setIsOpen] = useState(false)
    const [searchValue, setSearchValue] = useState("")

    useEffect(() => {
        setSearchValue(value)

        if (value.length > 0) {
            openAndFocus()
        }
    }, [value])

    useEffect(() => {
        if (isOpen && !hidden) {
            inputRef.current?.focus()
        }
    }, [isOpen, hidden])

    const handleOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value)
        onSearchDebounced.callback(e.target.value)
    }, [])

    const onSearchDebounced = useDebouncedCallback((str: string) => {
        onSearch(str)

        // To keep the input open and focused after the user manually deleted the query via backspace.
        if (str.length === 0) {
            executeAfterAsyncDomUpdates(openAndFocus)
        }
    }, DEBOUNCE_DURATION_LONG)

    const handleOnBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (e.target.value.length === 0) {
                setIsOpen(false)
            }
        },
        [isOpen],
    )

    const openAndFocus = useCallback(() => {
        setIsOpen(true)
        inputRef.current?.focus()
    }, [])

    return (
        <Search
            hidden={hidden}
            ref={inputRef}
            data-testid="searchInputField"
            className={`${isOpen ? "open" : ""} ${className}`}
            placeholder={t("placeholder:search")}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            onSearch={openAndFocus}
            value={searchValue}
            loading={isLoading}
            /* Not always true as we have small search button when no input and it won't fit two icons (search and delete) */
            allowClear={!!value}
        />
    )
}

export default React.memo(SearchInput)
